import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';

export interface TradingError {
  type: 'insufficient_balance' | 'order_failed' | 'network_error' | 'validation_error' | 'unknown';
  message: string;
  details?: string;
  orderDetails?: {
    side: 'buy' | 'sell';
    type: 'market' | 'limit' | 'stop';
    symbol: string;
    amount: number;
    price: number;
  };
}

interface TradingErrorContextType {
  error: TradingError | null;
  showError: (error: TradingError) => void;
  hideError: () => void;
  retryOrder: () => void;
  onRetry?: () => void;
  setOnRetry: (callback: (() => void) | undefined) => void;
}

const TradingErrorContext = createContext<TradingErrorContextType | undefined>(undefined);

export const useTradingError = () => {
  const context = useContext(TradingErrorContext);
  if (context === undefined) {
    throw new Error('useTradingError must be used within a TradingErrorProvider');
  }
  return context;
};

interface TradingErrorProviderProps {
  children: ReactNode;
}

export const TradingErrorProvider: React.FC<TradingErrorProviderProps> = ({ children }) => {
  const [error, setError] = useState<TradingError | null>(null);
  const [onRetry, setOnRetry] = useState<(() => void) | undefined>(undefined);

  const showError = useCallback((newError: TradingError) => {
    setError(newError);
  }, []);

  const hideError = useCallback(() => {
    setError(null);
    setOnRetry(undefined);
  }, []);

  const retryOrder = useCallback(() => {
    if (onRetry) {
      onRetry();
    }
    hideError();
  }, [onRetry, hideError]);

  const value: TradingErrorContextType = {
    error,
    showError,
    hideError,
    retryOrder,
    onRetry,
    setOnRetry,
  };

  return (
    <TradingErrorContext.Provider value={value}>
      {children}
    </TradingErrorContext.Provider>
  );
};
